import { string } from 'yup'

type Options = { httpsOnly?: boolean }

// RFC 1035 DNS limits.
const MAX_HOSTNAME_LENGTH = 253
const MAX_LABEL_LENGTH = 63

// A website hostname must be a public DNS name: at least two dot-separated labels
// (so a bare single label like `localhost` or `intranet` is rejected), each label
// 1-63 chars of [a-z0-9_-] with no leading/trailing hyphen, and <=253 chars total.
// Mirrors the backend rule in pbp-api `isValidWebsiteURL` so FE and BE agree.
const isValidHostname = (hostname: string) => {
  // A single trailing dot is the legal FQDN root label — `example.com.` === `example.com`.
  const withoutRootLabel = hostname.endsWith('.')
    ? hostname.slice(0, -1)
    : hostname

  if (withoutRootLabel.length > MAX_HOSTNAME_LENGTH) return false

  const labels = withoutRootLabel.split('.')
  if (labels.length < 2) return false

  return labels.every(
    (label) =>
      label.length > 0 &&
      label.length <= MAX_LABEL_LENGTH &&
      /^[a-z0-9_-]+$/.test(label) &&
      !label.startsWith('-') &&
      !label.endsWith('-')
  )
}

export const isValidWebsiteURL = (
  message = 'Must be a valid website URL',
  { httpsOnly = false }: Options = {}
) =>
  string()
    .transform((value) => {
      if (!value) return value
      if (!/^https?:\/\//.test(value)) {
        return httpsOnly ? `https://${value}` : `http://${value}`
      }
      return value
    })
    // Parse with `new URL` plus an explicit rule list instead of a single mega-regex,
    // so browser-valid shapes the regex over-rejected (a `//`-path, a space or bare `%`
    // in the path, a query-only URL, a trailing root dot) are accepted — matching the
    // backend after pbp-api #14212. The value returned is still the caller's transformed
    // text (never `url.href`), so IDN hostnames keep their unicode form.
    .test('is-website-url', message, async function (value) {
      // Yield a microtask so this test resolves AFTER yup's synchronous rules (notably a
      // chained `.max(255)`). Under yup 0.32's `abortEarly`, the first rule to *resolve*
      // with an error wins; without this yield a too-long invalid URL would surface this
      // message instead of the length error consumers assert. A bare `async` is NOT enough
      // on yup 0.32 — the body must actually await to defer to a later microtask.
      await Promise.resolve()

      if (!value) return true

      const fail = () => this.createError({ path: this.path, message })

      let url: URL
      try {
        url = new URL(value)
      } catch {
        return fail()
      }

      if (httpsOnly) {
        if (url.protocol !== 'https:') return fail()
      } else if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return fail()
      }

      // The raw authority substring, inspected directly rather than via `url.host` /
      // `url.username`: `new URL` normalises both an empty userinfo and a run of leading
      // slashes away, so the parsed URL no longer shows either of the shapes below.
      const authority = value.slice(value.indexOf('://') + 3).split(/[/?#]/)[0]

      // Reject slashes immediately after the scheme. A protocol-relative or root-relative
      // input (`//example.com`, `/example.com`) has no protocol for the transform to detect,
      // so it gets `http://` prepended -> `http:////example.com`, and `new URL` collapses the
      // run of slashes into a valid host. The backend disagrees: its transform regex never
      // matches a leading `/`, so the raw value reaches `new URL` unprefixed and throws,
      // 400ing the request. An empty authority substring is exactly this case.
      if (!authority) return fail()

      // Reject any userinfo — `user:pass@host`, `user@host`, even a bare `@host` (for which
      // `url.username` is '' because the empty userinfo was normalised away).
      if (authority.includes('@')) return fail()

      if (!isValidHostname(url.hostname)) return fail()

      return true
    })
